import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import type {
  ApiResponse,
  SalespersonCommission,
  CommissionPeriod,
  CommissionStats,
  CreditNoteSummary
} from "@/shared/types";

export class AuthenticatedCommissionService {
  constructor(private api: ReturnType<typeof useAuthenticatedApi>) {}

  private async fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const url = `/api${endpoint}`;
    const response = await this.api.request(url, {
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async getSalespersons(params?: {
    period?: { year: number; month: number };
    includeStats?: boolean;
    useAggregations?: boolean;
    isAdmin?: boolean;
  }): Promise<{
    salespersons: SalespersonCommission[];
    period: CommissionPeriod;
    stats?: CommissionStats;
    excludedSummary?: any;
    creditNoteSummary?: CreditNoteSummary;
  }> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", JSON.stringify(params.period));
    }
    if (params?.includeStats) {
      searchParams.set("includeStats", "true");
    }
    if (params?.useAggregations) {
      searchParams.set("useAggregations", "true");
    }
    // Note: isAdmin no longer needs to be passed as query parameter
    // Backend now checks role from authentication context

    const query = searchParams.toString();
    const endpoint = `/salespersons${query ? `?${query}` : ""}`;

    const response = await this.fetchAPI<{
      salespersons: SalespersonCommission[];
      period: CommissionPeriod;
      stats?: CommissionStats;
      excludedSummary?: any;
    }>(endpoint);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch salespersons");
    }

    return response.data!;
  }

  async getSalesperson(id: string, params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const searchParams = new URLSearchParams();

    if (params?.startDate) {
      searchParams.set("startDate", params.startDate);
    }
    if (params?.endDate) {
      searchParams.set("endDate", params.endDate);
    }

    const query = searchParams.toString();
    const endpoint = `/salesperson/${id}${query ? `?${query}` : ""}`;

    const response = await this.fetchAPI(endpoint);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch salesperson details");
    }

    return response.data;
  }

  async getPeriods(): Promise<CommissionPeriod[]> {
    const response = await this.fetchAPI<CommissionPeriod[]>("/periods");

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch periods");
    }

    return response.data!;
  }

  async getHealth(): Promise<any> {
    const response = await this.fetchAPI("/health");

    if (!response.success) {
      throw new Error(response.error || "Health check failed");
    }

    return response.data;
  }

  // NOTE: exportCommissions method removed - export is now handled client-side in Dashboard.tsx
  // This improves performance and user experience by avoiding redundant API calls

  async toggleInvoiceExclusion(params: {
    period: { year: number; month: number };
    invoiceNo: string;
    exclude: boolean;
  }): Promise<any> {
    const response = await this.fetchAPI("/invoice-exclusion", {
      method: "POST",
      body: JSON.stringify(params),
    });

    if (!response.success) {
      throw new Error(response.error || "Failed to toggle invoice exclusion");
    }

    return response.data;
  }

  async getExcludedInvoices(period: { year: number; month: number }): Promise<string[]> {
    const searchParams = new URLSearchParams();
    searchParams.set("period", JSON.stringify(period));

    const response = await this.fetchAPI<string[]>(`/excluded-invoices?${searchParams.toString()}`);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch excluded invoices");
    }

    return response.data!;
  }
}

// Create a hook to use the authenticated commission service
export function useAuthenticatedCommissionService() {
  const api = useAuthenticatedApi();
  return new AuthenticatedCommissionService(api);
}